import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import { useHandleApiCall, useNotification } from '../../hooks';
import {
  API_CONSTANTS,
  EXPIRY_TIME,
  extractDate,
  extractObjectId,
  extractTime,
  MediaTypeEnum,
  SHARED_VIDEO_ROUTE,
} from '../../utils';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';

interface SharingVideoModalProps {
  handleCloseSharingModal: () => void;
  videoId: string;
  open: boolean;
  url: string;
}

export const SharingVideoModal = ({
  handleCloseSharingModal,
  videoId,
  open,
  url,
}: SharingVideoModalProps) => {
  const { showNotification } = useNotification();
  const { post } = useHandleApiCall();
  const [shareUrl, setShareUrl] = useState('');
  const currentDate = new Date()
  const [expiresIn, setExpiresIn] = useState(new Date(currentDate.getTime() + Number(EXPIRY_TIME['7Days']) * 1000));
  const sharingMutation = post(API_CONSTANTS.SHARE_URL.key, API_CONSTANTS.SHARE_URL.path);

  const getShareUrl = (appendHost: boolean = true) => {
    const segments = shareUrl.split('/');
    let url: string;
    if (appendHost) {
      url = window.location.host + SHARED_VIDEO_ROUTE + '/' + segments[segments.length - 1];
    } else {
      url = SHARED_VIDEO_ROUTE + '/' + segments[segments.length - 1];
    }
    return url;
  };
  const copyUrl = () => {
    const url = getShareUrl();
    window.navigator.clipboard.writeText(url);
    showNotification('Copied!', 'success');
  };

  const [loading, setLoading] = useState(false);

  const fetchShareUrl = async () => {
    try {
      if (open) {
        setLoading(true);
        const objectId = extractObjectId(url);
        const resp = await sharingMutation.mutateAsync({
          objectId: objectId,
          format: MediaTypeEnum.VIDEO,
        });
        setExpiresIn(resp.expiresIn);
        setShareUrl(resp.shortPresignedUrl);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchShareUrl();
  }, [open]);

  return (
    <Dialog
      open={open}
      scroll={'paper'}
      onClick={(event) => event.stopPropagation()}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            // color: 'text.modal',
            backgroundColor: (theme) => theme.palette.background.secondary,
            borderStyle: 'solid',
            border: (theme) => `1px solid ${theme.palette.background.border}`,
            width: '400px',
            // padding: 0,
            // boxShadow: '0 0px 3px rgba(0, 0, 0, 0.2)',
          },
          '& .MuiDialogContent-root': {
            backgroundColor: (theme) => theme.palette.background.secondary,
          },
        },
      }}
    >
      <Box
        id="scroll-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          height: '72px',
          borderBottomWidth: '1px',
          borderBottomColor: (theme) => theme.palette.background.border,
        }}
      >
        <Typography
          sx={{ color: (theme) => theme.palette.background.activeBorder }}
          fontWeight={600}
          fontSize={20}
        >
          Share video
        </Typography>
        <CloseIcon sx={{ cursor: 'pointer' }} onClick={handleCloseSharingModal} />
      </Box>
      <DialogContent>
        <Box sx={{ textAlign: 'center' }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box>
              <Typography variant="caption">
                <a href={getShareUrl(false)} target="_blank">
                  {getShareUrl()}
                </a>
              </Typography>
              <ContentCopyIcon
                sx={{
                  fontSize: 22,
                  marginLeft: 1,
                  cursor: 'pointer',
                  color: (theme) => theme.palette.background.activeBorder,
                }}
                onClick={copyUrl}
              />
            </Box>
          )}
          <Box sx={{ marginTop: 2, textAlign: 'center' }}>
            <Alert
              severity="info"
              sx={{
                margin: 'auto',
                fontSize: 12,
                padding: '8px !important',
                width: '300px !important',
                color: (theme)=>  `${theme.palette.text.primary} !important`,
                backgroundColor: (theme)=>  `${theme.palette.background.border} !important`,
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2">
                  Anyone with this link can view your video. The link will remain active until{' '}
                  {extractTime(String(expiresIn))} on {extractDate(String(expiresIn))}.
                </Typography>
              </Box>
            </Alert>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
